import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import usePermiso from "./hooks/usePermiso";
import AppLayout from "./components/layout/AppLayout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Propiedades from "./pages/Propiedades";
import PropiedadForm from "./pages/PropiedadForm";
import LlavesFuera from "./pages/LlavesFuera";
import AceptarLlavesPublica from "./pages/AceptarLlavesPublica";
import Pipeline from "./pages/Pipeline";
import FichaCliente from "./pages/FichaCliente";
import Tareas from "./pages/Tareas";
import Monitor from "./pages/Monitor";
import Clientes from "./pages/Clientes";
import Contactos from "./pages/Contactos";
import Demandas from "./pages/Demandas";
import DemandaDetalle from "./pages/DemandaDetalle";
import Configuracion from "./pages/Configuracion";
import { ThemeProvider } from "./components/ThemeProvider";
import { DialogProvider } from "./context/DialogContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Agenda from "./pages/Agenda";
import Buscador from "./pages/Buscador";

import ConfigCamposMovil from "./pages/ConfigCamposMovil";
import ConexionWhatsApp from "./pages/ConexionWhatsApp";
import ConexionCorreo from "./pages/ConexionCorreo";
import NumerosTelnyx from "./pages/NumerosTelnyx";
import Ana from "./pages/Ana";
import GestionVentas from "./pages/GestionVentas";
import Carla from "./pages/CarlaPage";
import AnaConfig from "./pages/AnaConfig";
import CamposObligatoriosPropiedad from "./pages/CamposObligatoriosPropiedad";
import Usuarios from "./pages/Usuarios";
import TicketsSoporte from "./pages/TicketsSoporte";
import PermisosRoles from "./pages/PermisosRoles";
import Portales from "./pages/Portales";
import ConfiguracionScraping from "./pages/ConfiguracionScraping";
import PruebasScraping from "./pages/PruebasScraping";
import Pruebas from "./pages/Pruebas";
import CompararPortalPisosV2 from "./pages/CompararPortalPisosV2";
import CompararPortalIdealistaV2 from "./pages/CompararPortalIdealistaV2";
import CompararPortalHabitacliaV2 from "./pages/CompararPortalHabitacliaV2";
import CompararPortalFotocasaV2 from "./pages/CompararPortalFotocasaV2";
import ScrapingPortales from "./pages/ScrapingPortales";
import FelipeDashboard from "./pages/FelipeDashboard";
import PlantillasImpresion from "./pages/PlantillasImpresion";
import PlantillasDocumentacion from "./pages/PlantillasDocumentacion";
import PlantillasWhatsApp from "./pages/PlantillasWhatsApp";
import EscaparateManager from "./pages/EscaparateManager";
import Marco from "./pages/Marco";
import Promociones from "./pages/Promociones";
import PromocionAlta from "./pages/PromocionAlta";
import PresentacionCliente from "./pages/PresentacionCliente";
import PaginaEncargoGenerado from "./pages/PaginaEncargoGenerado";
import EmbedVistaPreviaPromocion from "./pages/EmbedVistaPreviaPromocion";
import { PresentacionClienteProvider } from "./context/PresentacionClienteContext";
import PresentacionMandoFlotante from "./components/presentacion/PresentacionMandoFlotante";
import { CitaModalOverlayProvider } from "./context/CitaModalOverlayContext";


/**
 * Protege una ruta por autenticación y, opcionalmente, por permiso de nodo.
 *
 * `nodeId` puede ser un string (se exige ese permiso) o un array de strings
 * (basta con que uno cualquiera de los nodos dé acceso — útil cuando una misma
 * página es alcanzable desde dos entradas de menú distintas con nodeIds
 * distintos, p. ej. "/configuracion/marco" vía el menú "Marco" (nodeId
 * "marco") o vía "Configuración > Marco" (nodeId "cfg_marco")).
 *
 * `parentNodeId`: si se indica (p. ej. "configuracion" o "plantillas"), hay
 * que tener "ver" también en el padre. Apagar Configuración oculta y bloquea
 * todas sus páginas, no solo el enlace del menú.
 *
 * Si no se pasa `nodeId`, solo se exige estar autenticado (comportamiento
 * previo, sin cambios). Si el usuario no tiene el permiso, se le redirige al
 * Dashboard en vez de mostrar la página — igual que ya ocurría con el enlace
 * del menú, pero ahora también bloquea el acceso directo por URL.
 */
function RutaProtegida({ children, nodeId, parentNodeId, cap = "ver" }) {
  const { usuario, cargando } = useAuth();
  const { puede } = usePermiso();
  if (cargando) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-500">Cargando...</p></div>;
  if (!usuario) return <Navigate to="/login" replace />;
  if (parentNodeId && !puede(parentNodeId, cap)) return <Navigate to="/dashboard" replace />;
  if (nodeId) {
    const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];
    const tieneAcceso = nodeIds.some((id) => puede(id, cap));
    if (!tieneAcceso) return <Navigate to="/dashboard" replace />;
  }
  return <AppLayout>{children}</AppLayout>;
}

// Ruta protegida sin sidebar (full-screen para Felipe)
function RutaFullscreen({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-500">Cargando...</p></div>;
  return usuario ? children : <Navigate to="/login" replace />;
}

function RutaPublica({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return null;
  return usuario ? <Navigate to="/dashboard" replace /> : children;
}

function RedirectLeadsToPipeline() {
  return <Navigate to="/pipeline" replace />;
}

function RedirectLeadIdToPipeline() {
  const { id } = useParams();
  return <Navigate to={`/pipeline?openLead=${encodeURIComponent(id)}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PresentacionClienteProvider>
        <ThemeProvider>
        <DialogProvider>
        <ErrorBoundary>
        <CitaModalOverlayProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<RutaPublica><Login /></RutaPublica>} />
          <Route path="/dashboard" element={<RutaProtegida><Dashboard /></RutaProtegida>} />
          <Route path="/propiedades" element={<RutaProtegida nodeId="propiedades"><Propiedades /></RutaProtegida>} />
          <Route path="/propiedades/llaves-fuera" element={<RutaProtegida nodeId="propiedades"><LlavesFuera /></RutaProtegida>} />
          <Route path="/propiedades/nueva" element={<RutaProtegida nodeId="propiedades"><PropiedadForm /></RutaProtegida>} />
          <Route path="/propiedades/:id/editar" element={<RutaProtegida nodeId="propiedades"><PropiedadForm /></RutaProtegida>} />
          <Route path="/llaves/a/:token" element={<AceptarLlavesPublica />} />
          <Route path="/leads" element={<RutaProtegida nodeId="pipeline"><RedirectLeadsToPipeline /></RutaProtegida>} />
          <Route path="/leads/:id" element={<RutaProtegida nodeId="pipeline"><RedirectLeadIdToPipeline /></RutaProtegida>} />
          <Route path="/pipeline" element={<RutaProtegida nodeId="pipeline"><Pipeline /></RutaProtegida>} />
          <Route path="/tareas" element={<RutaProtegida nodeId="tareas"><Tareas /></RutaProtegida>} />
          <Route path="/monitor" element={<RutaProtegida nodeId="monitor"><Monitor /></RutaProtegida>} />
          <Route path="/clientes" element={<RutaProtegida nodeId="ct_clientes"><Clientes /></RutaProtegida>} />
          <Route path="/clientes/:id" element={<RutaProtegida nodeId="contactos"><FichaCliente /></RutaProtegida>} />
          <Route path="/contactos" element={<RutaProtegida nodeId="contactos"><Contactos /></RutaProtegida>} />
          <Route path="/contactos/:id" element={<RutaProtegida nodeId="contactos"><FichaCliente /></RutaProtegida>} />
          <Route path="/demandas" element={<RutaProtegida nodeId="demandas"><Demandas /></RutaProtegida>} />
          <Route path="/demandas/:id" element={<RutaProtegida nodeId="demandas"><DemandaDetalle /></RutaProtegida>} />
          <Route path="/configuracion" element={<RutaProtegida nodeId="configuracion"><Configuracion /></RutaProtegida>} />
          <Route path="/agenda" element={<RutaProtegida nodeId="agenda"><Agenda /></RutaProtegida>} />
          <Route path="/buscador" element={<RutaProtegida nodeId="buscador"><Buscador /></RutaProtegida>} />
          <Route path="/ana" element={<RutaProtegida nodeId="ana"><Ana /></RutaProtegida>} />
          <Route path="/gestion-ventas" element={<RutaProtegida nodeId="gestion_ventas"><GestionVentas /></RutaProtegida>} />
          <Route path="/pruebas" element={<RutaProtegida nodeId="pruebas"><Pruebas /></RutaProtegida>} />
          <Route path="/carla" element={<RutaProtegida nodeId="carla"><Carla /></RutaProtegida>} />
          <Route path="/ana-config" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_ana"><AnaConfig /></RutaProtegida>} />
          <Route path="/config-campos-movil" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_campos_movil"><ConfigCamposMovil /></RutaProtegida>} />
          <Route path="/configuracion/whatsapp" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_whatsapp"><ConexionWhatsApp /></RutaProtegida>} />
          <Route path="/configuracion/correo" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_correo"><ConexionCorreo /></RutaProtegida>} />
          <Route path="/numeros-telnyx" element={<RutaProtegida nodeId="numeros_telnyx"><NumerosTelnyx /></RutaProtegida>} />
          <Route path="/configuracion/campos-obligatorios" element={<RutaProtegida parentNodeId="configuracion" nodeId="campos_obligatorios"><CamposObligatoriosPropiedad /></RutaProtegida>} />
          <Route path="/configuracion/usuarios" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_usuarios"><Usuarios /></RutaProtegida>} />
          <Route path="/soporte/tickets" element={<RutaProtegida nodeId="soporte_tickets_menu"><TicketsSoporte /></RutaProtegida>} />
          <Route path="/configuracion/permisos" element={<RutaProtegida parentNodeId="configuracion" nodeId="usuarios_roles"><PermisosRoles /></RutaProtegida>} />
          <Route path="/configuracion/portales" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_portales"><Portales /></RutaProtegida>} />
          <Route path="/configuracion/scraping" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_scraping_config"><ConfiguracionScraping /></RutaProtegida>} />
          <Route path="/scraping-portales" element={<RutaProtegida nodeId="scraping"><ScrapingPortales /></RutaProtegida>} />
          <Route path="/pruebas-scraping" element={<RutaProtegida nodeId="scr_pruebas"><PruebasScraping /></RutaProtegida>} />
          <Route path="/comparar-pisos-v2" element={<RutaProtegida nodeId="comparar"><CompararPortalPisosV2 /></RutaProtegida>} />
          <Route path="/comparar-idealista-v2" element={<RutaProtegida nodeId="comparar"><CompararPortalIdealistaV2 /></RutaProtegida>} />
          <Route path="/comparar-habitaclia-v2" element={<RutaProtegida nodeId="comparar"><CompararPortalHabitacliaV2 /></RutaProtegida>} />
          <Route path="/comparar-fotocasa-v2" element={<RutaProtegida nodeId="comparar"><CompararPortalFotocasaV2 /></RutaProtegida>} />
          <Route path="/felipe" element={<RutaProtegida nodeId="admin_felipe"><FelipeDashboard /></RutaProtegida>} />
          <Route path="/admin/felipe" element={<RutaProtegida nodeId="admin_felipe"><FelipeDashboard /></RutaProtegida>} />
          <Route path="/plantillas/impresion" element={<RutaProtegida parentNodeId="plantillas" nodeId="pla_impresion"><PlantillasImpresion /></RutaProtegida>} />
          <Route path="/plantillas/documentacion" element={<RutaProtegida parentNodeId="plantillas" nodeId="pla_documentacion"><PlantillasDocumentacion /></RutaProtegida>} />
          <Route path="/plantillas/whatsapp" element={<RutaProtegida parentNodeId="plantillas" nodeId="pla_whatsapp"><PlantillasWhatsApp /></RutaProtegida>} />
          <Route path="/configuracion/escaparate" element={<RutaProtegida parentNodeId="configuracion" nodeId="cfg_escaparate"><EscaparateManager /></RutaProtegida>} />
          <Route path="/configuracion/marco" element={<RutaProtegida nodeId={["marco", "cfg_marco"]}><Marco /></RutaProtegida>} />
          <Route path="/promociones" element={<RutaProtegida nodeId="promociones"><Promociones /></RutaProtegida>} />
          <Route path="/promociones/nueva" element={<RutaProtegida nodeId="promociones"><PromocionAlta /></RutaProtegida>} />
          <Route path="/promociones/:ref" element={<RutaProtegida nodeId="promociones"><PromocionAlta /></RutaProtegida>} />
          <Route path="/configuracion/video-marco" element={<Navigate to="/configuracion/marco" replace />} />
          <Route path="/presentacion/:propiedadId" element={<RutaFullscreen><PresentacionCliente /></RutaFullscreen>} />
          <Route path="/propiedades/:id/encargo-generado" element={<RutaFullscreen><PaginaEncargoGenerado /></RutaFullscreen>} />
          {/* Embed Alta promoción → reutiliza FichaResumenPanel (postMessage desde mockup) */}
          <Route path="/embed/vista-previa-promocion" element={<EmbedVistaPreviaPromocion />} />
        </Routes>
        </CitaModalOverlayProvider>
        </ErrorBoundary>
        <PresentacionMandoFlotante />
        </DialogProvider>
      </ThemeProvider>
        </PresentacionClienteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}